import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input, OnChanges,
    OnDestroy,
    OnInit,
    Output, SimpleChanges,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {v4 as uuid} from 'uuid';
import {map, startWith, takeUntil} from 'rxjs/operators';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {faTimes} from '@fortawesome/pro-light-svg-icons';

@Component({
    selector: 'app-autocomplete-dropdown',
    templateUrl: './autocomplete-dropdown.component.html',
    styleUrls: ['./autocomplete-dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteDropdownComponent),
            multi: true
        }
    ]
})
export class AutocompleteDropdownComponent<T extends Exclude<unknown, string>> implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

    private onChangeFn: (_: any) => void;
    private destroyed = new Subject<void>();

    @ViewChild('searchControlView') protected searchControlElement: ElementRef;
    readonly searchControl: FormControl = new FormControl();
    readonly uniqueId = uuid();
    readonly faTimes = faTimes;

    disabled = false;
    filteredValues: Observable<T[]>;

    @Input() value: T = null;
    @Output() valueChange: EventEmitter<T> = new EventEmitter<T>();
    @Input() values: T[] = [];
    @Input() placeholder = '';
    @Input() triggerDisplayFn: (value: T) => string = value => (value as string) ?? '';
    @Input() valueDisplayFn: (value: T) => string = value => value as string;
    @Input() valueSearchFn: (value: T) => string = value => value as string;

    constructor() {
    }

    ngOnInit() {
        this.filteredValues = this.searchControl.valueChanges.pipe(
            startWith(''),
            map(event => !event || typeof event !== 'string' ? this.values : this.filterValues(event)),
            takeUntil(this.destroyed)
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('value' in changes) {
            this.alignSelectedValue(changes.value.currentValue);
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    registerOnChange(fn: any): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(_: any): void {
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(obj: any): void {
        this.alignSelectedValue(obj);
    }

    onSelectValue($event: MatAutocompleteSelectedEvent) {
        this.searchControlElement.nativeElement.blur(); // remove keyboard focus upon selection through mouse click
        this.notifySelectionChange($event.option.value);
    }

    onClickResetSearch() {
        this.resetSearch();
    }

    onSearchLoseFocus($event: FocusEvent) {
        // only reset control if no actual value has just been selected
        if (!$event.relatedTarget || ($event.relatedTarget as Element).getAttribute('data-autocomplete-dropdown-option-id') !== this.uniqueId) {
            if (!this.searchControl.value || typeof this.searchControl.value === 'string') {
                // we have lost focus on (autocomplete) input, but not because we have selected an actual option:
                // simulate a search reset
                this.resetSearch();
            }
        }
    }

    private resetSearch() {
        this.searchControl.setValue(null);
        this.notifySelectionChange(null);
    }

    private filterValues(valueStr: string): T[] {
        const filterValue = valueStr.trim().toLowerCase();
        return this.values.filter(value => this.valueSearchFn(value).toLowerCase().includes(filterValue));
    }

    private alignSelectedValue(value: T) {
        this.searchControl.setValue(value);
    }

    private notifySelectionChange(value: T) {
        if (this.onChangeFn) {
            this.onChangeFn(value);
        }
        this.valueChange.next(value);
    }
}
